/* eslint-disable */
// @ts-nocheck
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import * as React from 'react';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { css, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import InlineMessage from '@atlaskit/inline-message';
import SectionMessage from '@atlaskit/section-message/message';
import SectionMessageAction from '@atlaskit/section-message/message-action';
import { token } from '@atlaskit/tokens';
import {
	FormattedMessage,
	RawIntlProvider,
	createIntl,
	createIntlCache,
	injectIntl,
} from 'react-intl';
import { logger } from '../../adaptors/logger/LoggerAdaptor';
import { connectEosBannerMessages } from './messages';
import type { ConnectToForgeMigration } from './ConnectIframe';

const DYNAMIC_CONFIG_DISMISS_DAYS = 'customer-messaging-iframe-banner-dismiss-days';
const DYNAMIC_CONFIG_BLOCKED_APP_KEYS = 'customer_messaging_apps_blocked_by_atlassian';
const DISMISS_STORAGE_KEY_SUFFIX = 'connect-eos-banner_dismissed';
const BSC_SUCCESS_STATUS = 'SUCCESS';
const BSC_BLOCKED_STATUS = 'BLOCKED';
const DEFAULT_DISMISS_DAYS = 30;
const MS_PER_DAY: number = 24 * 60 * 60 * 1000;

const FEATURE_GATE_BANNER_ENABLED = 'customer-messaging-iframe-banner-enabled';
const FEATURE_GATE_PHASE_2 = 'customer_messaging_iframes_phase_2';
const FEATURE_GATE_PHASE_3 = 'customer_messaging_iframes_phase_3';
const FEATURE_GATE_DEV_CANARY_ENABLED = 'customer-messaging-is-dev-canary';
const DEVELOPER_CANARY_PROGRAM_URL =
	'https://developer.atlassian.com/cloud/jira/platform/developer-canary-program/';
const FORGE_CHANGELOG_URL = 'https://developer.atlassian.com/platform/forge/changelog/';
const DEFAULT_LOCALE = 'en';
const defaultIntlCache = createIntlCache();
const defaultIntl = createIntl({ locale: DEFAULT_LOCALE }, defaultIntlCache);

interface ConnectEosBannerProps {
	appKey?: string;
	appName?: string;
	cloudId?: string;
	isSiteAdmin?: boolean;
	isSystemApp?: boolean;
	moduleType?: string;
	connectToForgeMigration?: ConnectToForgeMigration;
}

type MigrationCohort = 'UNDECLARED' | 'NO' | 'UNDECIDED' | 'YES';

interface ConnectEosBannerState {
	dismissed: boolean;
}

const containerStyles: React.CSSProperties = {
	marginBottom: '8px',
};

const devCanaryMessageStyles = css({
	marginTop: token('space.0'),
});

function getDismissStorageKey(appKey: string): string {
	return `${appKey}-${DISMISS_STORAGE_KEY_SUFFIX}`;
}

class ConnectEosBannerComponent extends React.PureComponent<
	ConnectEosBannerProps,
	ConnectEosBannerState
> {
	static defaultProps = {
		isSystemApp: false,
		moduleType: '',
	};

	constructor(props: ConnectEosBannerProps) {
		super(props);
		this.state = {
			dismissed: this._isDismissed(),
		};
	}

	_getDismissDurationMs(): number {
		const days = FeatureGates.getExperiment(DYNAMIC_CONFIG_DISMISS_DAYS, {
			fireExperimentExposure: false,
		}).get('value', DEFAULT_DISMISS_DAYS);
		return (typeof days === 'number' && days > 0 ? days : DEFAULT_DISMISS_DAYS) * MS_PER_DAY;
	}

	_isDismissed(): boolean {
		if (typeof window === 'undefined') {
			return false;
		}

		const dismissStorageKey = getDismissStorageKey(this.props.appKey);
		const storedItem = AtlBrowserStorageLocal.getItem(dismissStorageKey);
		if (!storedItem) {
			return false;
		}

		let dismissedAt: number | string | undefined;
		try {
			const parsedItem = JSON.parse(storedItem);
			dismissedAt = parsedItem.value;
		} catch (e) {
			logger.warn('Failed to parse Connect EOS banner dismissal', {
				key: dismissStorageKey,
				error: e,
			});
			return false;
		}

		if (!dismissedAt) {
			return false;
		}
		const dismissedTimestamp = Number(dismissedAt);
		if (isNaN(dismissedTimestamp)) {
			return false;
		}

		return Date.now() - dismissedTimestamp < this._getDismissDurationMs();
	}

	_dismiss = (): void => {
		const dismissedAt = Date.now();
		const storedItem = JSON.stringify({
			value: dismissedAt,
		});
		const dismissStorageKey = getDismissStorageKey(this.props.appKey);

		// @browser-storage setter
		void AtlBrowserStorageLocal.setItem(dismissStorageKey, storedItem)
			.then((result) => {
				if (
					result?.status &&
					result.status !== BSC_SUCCESS_STATUS &&
					result.status !== BSC_BLOCKED_STATUS
				) {
					logger.warn('Failed to persist Connect EOS banner dismissal', {
						key: dismissStorageKey,
						result,
					});
				}
			})
			.catch((error) => {
				logger.warn('Failed to persist Connect EOS banner dismissal', {
					key: dismissStorageKey,
					error,
				});
			});
		this.setState({ dismissed: true });
	};

	_shouldShowBanner(): boolean {
		// Don't show if the main feature gate is not enabled
		if (!FeatureGates.checkGate(FEATURE_GATE_BANNER_ENABLED)) {
			return false;
		}

		// Don't show for system apps
		if (this.props.isSystemApp) {
			return false;
		}

		if (this._isBlockedByAtlassian()) {
			return false;
		}

		const phaseGate = this._getPhaseGate();
		if (phaseGate && !FeatureGates.checkGate(phaseGate)) {
			return false;
		}

		// Don't show if previously dismissed (within configured dismiss duration)
		if (this.state.dismissed) {
			return false;
		}

		return true;
	}

	_getMigrationCohort(): MigrationCohort {
		const migration = this.props.connectToForgeMigration;
		if (!migration?.defined) {
			return 'UNDECLARED';
		}

		switch (migration.willMigrateToForgeBeforeEOS) {
			case 'NO':
			case 'YES':
			case 'UNDECIDED':
				return migration.willMigrateToForgeBeforeEOS;
			default:
				return 'UNDECIDED';
		}
	}

	_getPhaseGate(): string | undefined {
		switch (this._getMigrationCohort()) {
			case 'NO':
				return undefined;
			case 'YES':
				return FEATURE_GATE_PHASE_3;
			case 'UNDECLARED':
			case 'UNDECIDED':
			default:
				return FEATURE_GATE_PHASE_2;
		}
	}

	_isBlockedByAtlassian(): boolean {
		const blockedAppKeys = FeatureGates.getExperiment(DYNAMIC_CONFIG_BLOCKED_APP_KEYS, {
			fireExperimentExposure: false,
		}).get('value', []);

		return (
			Array.isArray(blockedAppKeys) &&
			typeof this.props.appKey === 'string' &&
			blockedAppKeys.includes(this.props.appKey)
		);
	}

	_isProd(): boolean {
		if (typeof window === 'undefined') {
			return false;
		}

		return window.location.hostname.endsWith('.atlassian.net');
	}

	_isSiteAdmin(): boolean {
		return this.props.isSiteAdmin ?? false;
	}

	_isDevCanary(): boolean {
		return FeatureGates.checkGate(FEATURE_GATE_DEV_CANARY_ENABLED);
	}

	_getConnectedAppsUrl(): string | undefined {
		const cloudId =
			this.props.cloudId ??
			(typeof document !== 'undefined'
				? (document.querySelector('meta[name="ajs-cloud-id"]') as HTMLMetaElement)?.content
				: undefined) ??
			undefined;
		const adminHost = this._isProd() ? 'admin.atlassian.com' : 'admin.stg.atlassian.com';

		if (!cloudId) {
			return undefined;
		}
		return `https://${adminHost}/s/${cloudId}/user-connected-apps/tab/installed`;
	}

	_getMessageBody(): React.ReactElement<any> | null {
		const { appName } = this.props;
		const isSiteAdmin = this._isSiteAdmin();
		const connectedAppsUrl = this._getConnectedAppsUrl();
		const migrationGuideUrl = this.props.connectToForgeMigration?.migrationGuideUrl?.trim();
		const cohort = this._getMigrationCohort();
		const messageCohort = cohort !== 'UNDECLARED' && !migrationGuideUrl ? 'UNDECLARED' : cohort;
		const messages = {
			UNDECLARED: isSiteAdmin
				? connectEosBannerMessages.undeclaredAdmin
				: connectEosBannerMessages.undeclaredNonAdmin,
			NO: isSiteAdmin ? connectEosBannerMessages.noAdmin : connectEosBannerMessages.noNonAdmin,
			UNDECIDED: isSiteAdmin
				? connectEosBannerMessages.undecidedAdmin
				: connectEosBannerMessages.undecidedNonAdmin,
			YES: isSiteAdmin ? connectEosBannerMessages.yesAdmin : connectEosBannerMessages.yesNonAdmin,
		};
		const connectedApps = (chunks: React.ReactNode[]) => (
			<strong>
				{connectedAppsUrl ? <a href={connectedAppsUrl}>{chunks}</a> : <span>{chunks}</span>}
			</strong>
		);
		const migrationGuide = (chunks: React.ReactNode[]) =>
			migrationGuideUrl ? (
				<a href={migrationGuideUrl} target="_blank" rel="noopener noreferrer">
					{chunks}
				</a>
			) : (
				<span>{chunks}</span>
			);
		const strong = (chunks: React.ReactNode[]) => <strong>{chunks}</strong>;

		return (
			<p>
				<FormattedMessage
					{...messages[messageCohort]}
					values={{
						appName: appName ?? '',
						hasAppName: Boolean(appName),
						strong,
						connectedApps,
						migrationGuide,
					}}
				/>
			</p>
		);
	}

	_getDevCanaryMessageBody(): React.ReactElement<any> | null {
		if (!this._isDevCanary()) {
			return null;
		}

		const developerCanaryProgram = (chunks: React.ReactNode[]) => (
			<a href={DEVELOPER_CANARY_PROGRAM_URL} target="_blank" rel="noopener noreferrer">
				{chunks}
			</a>
		);
		const forgeChangelog = (chunks: React.ReactNode[]) => (
			<a href={FORGE_CHANGELOG_URL} target="_blank" rel="noopener noreferrer">
				{chunks}
			</a>
		);

		return (
			<p css={devCanaryMessageStyles}>
				<FormattedMessage
					{...connectEosBannerMessages.devCanaryMessage}
					values={{ developerCanaryProgram, forgeChangelog }}
				/>
			</p>
		);
	}

	_getDismissAction(): React.ReactElement<any> {
		return (
			<SectionMessageAction onClick={this._dismiss} key="dismiss">
				<FormattedMessage {...connectEosBannerMessages.dismiss} />
			</SectionMessageAction>
		);
	}

	getSectionWarningBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-banner" style={containerStyles}>
				<SectionMessage appearance="warning">
					{this._getMessageBody()}
					{this._getDevCanaryMessageBody()}
					{this._getDismissAction()}
				</SectionMessage>
			</div>
		);
	}

	getInlineBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-v2-banner">
				<InlineMessage
					appearance="warning"
					secondaryText={(this.props.intl ?? defaultIntl).formatMessage(
						connectEosBannerMessages.inlineSecondaryText,
					)}
				>
					{this._getMessageBody()}
					{this._getDevCanaryMessageBody()}
					{this._getDismissAction()}
				</InlineMessage>
			</div>
		);
	}

	render(): React.ReactElement<any> | null {
		if (!this._shouldShowBanner()) {
			return null;
		}

		const banner =
			this.props.moduleType === 'webPanels'
				? this.getInlineBanner()
				: this.getSectionWarningBanner();

		if (!this.props.intl) {
			return <RawIntlProvider value={defaultIntl}>{banner}</RawIntlProvider>;
		}

		return banner;
	}
}

const ConnectEosBanner: React.ComponentType<ConnectEosBannerProps> = injectIntl(
	ConnectEosBannerComponent,
	{ enforceContext: false },
);

export {
	ConnectEosBanner,
	DISMISS_STORAGE_KEY_SUFFIX,
	DEFAULT_DISMISS_DAYS,
	DYNAMIC_CONFIG_DISMISS_DAYS,
	DYNAMIC_CONFIG_BLOCKED_APP_KEYS,
	FEATURE_GATE_PHASE_2,
	FEATURE_GATE_PHASE_3,
	MS_PER_DAY,
	FEATURE_GATE_BANNER_ENABLED,
	getDismissStorageKey,
};
